import { events } from "@constants/tracking";
import { useDevice } from "@hooks/useDevice";
import { getFeedbackReasonsByType } from "@memorang/helpers/feedback";
import type { IconType } from "@memorang/ui/icon";
import { trackCustomEvent } from "analytics";
import { router } from "expo-router";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton, Menu } from "react-native-paper";
import { FeedbackDialogContainer } from "../../../containers/FeedbackDialogContainer";
import useAIFeedbackStore from "../hooks/useAIFeedbackStore";
import useSessionStore from "../hooks/useSessionStore";

type FeedbackType = "positive" | "negative" | "reportIssue";

type UseAIFeedbackMenuProps = {
	itemId: number;
	featureName: "AI Feedback" | "AI Hint";
	disabled?: boolean;
};

export const AIFeedbackMenu = ({
	itemId,
	featureName,
	disabled,
}: UseAIFeedbackMenuProps) => {
	const { t } = useTranslation();
	const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
	const [feedbackType, setFeedbackType] = useState<FeedbackType>();
	const [menuVisible, setMenuVisible] = useState(false);
	const isSummativeTest = useSessionStore((state) => state.isSummativeTest);
	const { isMobile } = useDevice();

	const itemAIFeedback = useAIFeedbackStore(
		(store) => store.aiResponse?.[itemId],
	);
	const sessionId = useSessionStore((state) => state.id);

	const negativeReasonList = useMemo(
		() => getFeedbackReasonsByType<IconType>("negative", t),
		[t],
	);

	const reportReasonList = useMemo(
		() => getFeedbackReasonsByType<IconType>("report", t),
		[t],
	);

	const handleFeedback = useCallback(
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
		(type: FeedbackType) => {
			if (isMobile) {
				router.push({
					pathname: "/feedback",
					params: {
						view: "session",
						itemId: String(itemId),
						featureName,
						feedbackTitle:
							type === "reportIssue" ? t("dialog.report.title") : undefined,
						feedbackSubtitle:
							type === "reportIssue"
								? t("dialog.report.body")
								: type === "negative"
									? t("dialog.feedback.negativeBody")
									: type === "positive"
										? t("dialog.feedback.positiveBody")
										: undefined,
						feedbackIcon: type === "reportIssue" ? "flag-outline" : undefined,
						itemFeedbackRating: type === "reportIssue" ? "negative" : type,
						sessionId: String(sessionId),
						threadId: itemAIFeedback?.runDetails?.threadId,
						runId: itemAIFeedback?.runDetails?.runId,
						type: type === "reportIssue" ? "Content Flagged" : undefined,
						feedbackReasonType:
							type === "negative"
								? "negative"
								: type === "reportIssue"
									? "report"
									: undefined,
					},
				});
			} else {
				setFeedbackType(type);
				setShowFeedbackDialog(true);
			}
			setMenuVisible(false);
			trackCustomEvent({
				eventName:
					type === "reportIssue"
						? events.aiFeedbackReport
						: type === "positive"
							? events.aiFeedbackPositive
							: events.aiFeedbackNegative,
				itemId,
			});
		},
		[
			itemId,
			itemAIFeedback?.runDetails?.threadId,
			itemAIFeedback?.runDetails?.runId,
			sessionId,
			t,
			featureName,
			isMobile,
		],
	);
	const handleDismissFeedback = useCallback(() => {
		setShowFeedbackDialog(false);
	}, []);

	const isReportIssue = feedbackType === "reportIssue";
	const isNegative = feedbackType === "negative";
	const isPositive = feedbackType === "positive";

	const feedbackReasonList = isReportIssue
		? reportReasonList
		: isNegative
			? negativeReasonList
			: undefined;

	const getSubtitle = () => {
		if (isReportIssue) {
			return t("dialog.report.body");
		}
		if (isNegative) {
			return t("dialog.feedback.negativeBody");
		}
		if (isPositive) {
			return t("dialog.feedback.positiveBody");
		}
		return undefined;
	};

	return (
		<>
			<Menu
				visible={menuVisible}
				onDismiss={() => setMenuVisible(false)}
				anchor={
					<IconButton
						icon="dots-vertical"
						size={18}
						onPress={() => setMenuVisible(true)}
						disabled={disabled}
						style={{ margin: 0 }}
					/>
				}
				anchorPosition="bottom"
			>
				<Menu.Item
					leadingIcon="thumb-up-outline"
					onPress={() => handleFeedback("positive")}
					title={t("report.sessionAnalysis.menuActions.positiveFeedback")}
				/>
				<Menu.Item
					leadingIcon="thumb-down-outline"
					onPress={() => handleFeedback("negative")}
					title={t("report.sessionAnalysis.menuActions.negativeFeedback")}
				/>
				<Menu.Item
					leadingIcon="flag-outline"
					onPress={() => handleFeedback("reportIssue")}
					title={t("report.sessionAnalysis.menuActions.reportIssue")}
				/>
			</Menu>
			<FeedbackDialogContainer
				visible={showFeedbackDialog}
				onDismiss={handleDismissFeedback}
				title={isReportIssue ? t("dialog.report.title") : undefined}
				subtitle={getSubtitle()}
				icon={isReportIssue ? "flag-outline" : undefined}
				cta={isReportIssue ? t("dialog.report.cta") : undefined}
				itemId={String(itemId)}
				sessionId={sessionId?.toString()}
				runId={itemAIFeedback?.runDetails?.runId}
				rating={isReportIssue ? "negative" : feedbackType}
				type={isReportIssue ? "Content Flagged" : "Review"}
				sessionType={isSummativeTest ? "SUMMATIVE" : "FORMATIVE"}
				threadId={itemAIFeedback?.runDetails?.threadId}
				feedbackReasonList={feedbackReasonList}
			/>
		</>
	);
};
